/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Switch,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";

interface TableProps {
  columns: string[];
  columnHeaders: string[];
  data: Array<Record<string, any>>;
  setSelectedOrder: React.Dispatch<
    React.SetStateAction<{ id: number; status: string } | null>
  >;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  onVendorDisabled?: () => void;
}

const VendorTableComponent: React.FC<TableProps> = ({
  columns,
  columnHeaders,
  data: initialData,
  setSelectedOrder,
  currentPage,
  setCurrentPage,
  totalPages,
  onVendorDisabled,
}) => {
  const [tableData, setTableData] = useState(initialData);
  const [togglingVendorId, setTogglingVendorId] = useState<number | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingVendor, setPendingVendor] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [statusError, setStatusError] = useState("");
  const navigate = useNavigate();

  React.useEffect(() => {
    setTableData(initialData);
  }, [initialData]);

  const handleStatusChange = async (id: number) => {
    setTogglingVendorId(id);

    try {
      await GetAllVendorsListApi.api.changeVendorStatus(id);
      onVendorDisabled?.();
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        "Unable to change vendor status. Please try again.";
      setStatusError(message);
      console.error(`Failed to change vendor status for vendor ${id}`, error);
    } finally {
      setTogglingVendorId(null);
    }
  };

  const handleOpenDisableConfirm = (id: number, name: string) => {
    setPendingVendor({ id, name });
    setConfirmDialogOpen(true);
  };

  const handleCloseDisableConfirm = () => {
    if (togglingVendorId !== null) {
      return;
    }

    setConfirmDialogOpen(false);
    setPendingVendor(null);
  };

  const handleConfirmDisable = async () => {
    if (!pendingVendor) {
      return;
    }

    await handleStatusChange(pendingVendor.id);
    setConfirmDialogOpen(false);
    setPendingVendor(null);
  };

  // Calculate the correct row number based on items per page (10)
  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-hidden">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              {columnHeaders.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => {
                const rowNumber = getRowNumber(rowIndex);
                
                return (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-4 py-4 text-sm text-gray-900">
                        {column === "enabled" ? (
                          <div className="flex items-center">
                            <Switch
                              checked={Boolean(row.enabled)}
                              disabled={togglingVendorId === row.id}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (row.enabled) {
                                  handleOpenDisableConfirm(
                                    row.id,
                                    row.name ?? "this vendor"
                                  );
                                } else {
                                  handleStatusChange(row.id);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              color="primary"
                              size="small"
                              inputProps={{ "aria-label": "status toggle" }}
                            />
                          </div>
                        ) : column === "address" ? (
                          <span>
                            {row.address 
                              ? `${row.address.addressLineOne}, ${row.address.city}, ${row.address.country}`
                              : "N/A"}
                          </span>
                        ) : (
                          <span>
                            {row[column] !== undefined
                              ? row[column]?.toString()
                              : "N/A"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columnHeaders.length + 1}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">No vendors found</p>
                    <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDisableConfirm}
        aria-labelledby="disable-vendor-dialog-title"
        aria-describedby="disable-vendor-dialog-description"
        maxWidth="xs"
        fullWidth>
        <DialogTitle id="disable-vendor-dialog-title">
          Disable vendor?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="disable-vendor-dialog-description">
            {pendingVendor
              ? `Are you sure you want to disable ${pendingVendor.name}? This vendor will be removed from the active list.`
              : "Are you sure you want to disable this vendor? This vendor will be removed from the active list."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDisableConfirm}
            disabled={togglingVendorId !== null}
            color="inherit"
            variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisable}
            disabled={togglingVendorId !== null}
            color="error"
            variant="contained">
            {togglingVendorId !== null ? "Disabling..." : "Yes, Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(statusError)}
        autoHideDuration={6000}
        onClose={() => setStatusError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setStatusError("")}
          severity="error"
          variant="filled">
          {statusError}
        </Alert>
      </Snackbar>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>
            
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Next Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorTableComponent;